"use client";

import { useEffect, useMemo, useState } from "react";
import { submitBooking, trackMetric } from "@/app/actions/public";
import { PublicForm } from "@/components/PublicForm";

type Service = { id: string; name: string; durationMin: number; roles: string[] };
type Provider = { id: string; name: string; role: string };
type Slot = { start: string; label: string };

export function BookFlow({
  services,
  providers,
  today,
  maxDate,
  source,
  utmSource,
  utmMedium,
  utmCampaign,
}: {
  services: Service[];
  providers: Provider[];
  today: string;
  maxDate: string;
  source: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}) {
  const [serviceId, setServiceId] = useState(services[0]?.id ?? "");
  const [providerId, setProviderId] = useState("");
  const [dateStr, setDateStr] = useState(today);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [hoursLabel, setHoursLabel] = useState("");
  const [start, setStart] = useState("");
  const [loading, setLoading] = useState(false);

  const service = services.find((row) => row.id === serviceId);
  const matchingProviders = useMemo(
    () => providers.filter((row) => (service ? service.roles.includes(row.role) : true)),
    [providers, service],
  );

  useEffect(() => {
    if (!matchingProviders.some((row) => row.id === providerId)) {
      setProviderId(matchingProviders[0]?.id ?? "");
    }
  }, [matchingProviders, providerId]);

  useEffect(() => {
    if (!providerId || !serviceId || !dateStr) return;
    let cancelled = false;
    setLoading(true);
    setHoursLabel("");
    const params = new URLSearchParams({ providerId, serviceId, date: dateStr });
    fetch(`/api/slots?${params}`)
      .then((res) => res.json())
      .then((data: { slots?: Slot[]; hoursLabel?: string }) => {
        if (!cancelled) {
          setSlots(data.slots ?? []);
          setHoursLabel(data.hoursLabel ?? "");
          setStart("");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [providerId, serviceId, dateStr]);

  return (
    <div className="grid grid-cols-1 gap-6">
      <fieldset className="grid grid-cols-1 gap-4">
        <label className="grid gap-1 text-base">
          Service
          <select
            className="field"
            value={serviceId}
            onChange={(event) => setServiceId(event.target.value)}
          >
            {services.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name} ({row.durationMin} min)
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-base">
          Provider
          <select
            className="field"
            value={providerId}
            onChange={(event) => setProviderId(event.target.value)}
          >
            {matchingProviders.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-base">
          Date
          <input
            type="date"
            className="field"
            value={dateStr}
            min={today}
            max={maxDate}
            onChange={(event) => setDateStr(event.target.value)}
          />
        </label>
      </fieldset>

      <div>
        <p className="mb-2 text-base font-medium">Open slots</p>
        {loading ? <p className="text-base">Loading times…</p> : null}
        {!loading && hoursLabel ? <p className="muted mb-2 text-base">{hoursLabel}</p> : null}
        {!loading && slots.length === 0 ? (
          <p className="muted text-base">
            {hoursLabel === "Time off."
              ? "No times are offered while this provider is away."
              : hoursLabel.startsWith("Closed")
                ? "No times are offered on a closed day."
                : "No open times on this day. Try another date."}
          </p>
        ) : (
          <div className="@container min-w-0">
          <div className="grid grid-cols-2 gap-2 @min-[28rem]:grid-cols-4">
            {slots.map((slot) => (
              <button
                key={slot.start}
                type="button"
                onClick={() => {
                  setStart(slot.start);
                  void trackMetric("slot_chosen", source, "/book");
                }}
                className={`min-h-11 border-2 px-3 text-base uppercase tracking-wide ${
                  start === slot.start
                    ? "border-brand-primary bg-brand-primary text-button-text"
                    : "border-brand-fog/50 bg-transparent text-brand-white"
                }`}
              >
                {slot.label}
              </button>
            ))}
          </div>
          </div>
        )}
      </div>

      {start ? (
        <PublicForm
          action={submitBooking}
          submitLabel="Confirm booking"
          success="Your request is in. The clinic will confirm the time with you."
        >
          <input type="hidden" name="serviceId" value={serviceId} />
          <input type="hidden" name="providerId" value={providerId} />
          <input type="hidden" name="start" value={start} />
          <input type="hidden" name="source" value={source} />
          <input type="hidden" name="utmSource" value={utmSource || ""} />
          <input type="hidden" name="utmMedium" value={utmMedium || ""} />
          <input type="hidden" name="utmCampaign" value={utmCampaign || ""} />
          <label className="grid gap-1 text-base">
            Name
            <input
              name="name"
              required
              className="field"
            />
          </label>
          <label className="grid gap-1 text-base">
            Email
            <input
              name="email"
              type="email"
              required
              className="field"
            />
          </label>
          <label className="grid gap-1 text-base">
            Phone
            <input
              name="phone"
              required
              className="field"
            />
          </label>
          <label className="grid gap-1 text-base">
            Notes (optional)
            <textarea
              name="notes"
              rows={3}
              className="field"
            />
          </label>
        </PublicForm>
      ) : (
        <p className="muted text-base">Choose a time to enter your contact details.</p>
      )}
    </div>
  );
}
